import React, { useState, useEffect, useRef } from "react";
import { useClerk, useSignIn, useUser } from "@clerk/clerk-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import LOGO from "../assets/images/Diligent - 2025 Branding 2.svg"; // Adjust the path as necessary
import { clearAllStickyFilters } from "../utils/stickyFilterStorage";

const ResetPasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isConsumingTicket, setIsConsumingTicket] = useState(false);
  const ticketHandledRef = useRef(false);
  const signedOutForInviteRef = useRef(false);
  const { user, isLoaded: isUserLoaded } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { isLoaded, signIn, setActive } = useSignIn();
  const [searchParams] = useSearchParams();

  // Invite emails embed Clerk sign-in token (ticket strategy). Legacy links may still use ?email=.
  const signInToken = searchParams.get("token");
  const email = searchParams.get("email");

  // Check if passwords match whenever either password field changes
  useEffect(() => {
    if (confirmPassword) {
      setPasswordsMatch(password === confirmPassword);
    } else {
      setPasswordsMatch(true); // Don't show error when confirm field is empty
    }
  }, [password, confirmPassword]);

  // Consume Clerk sign-in token so the invitee has a session before updatePassword.
  // If another session is already active (e.g. tester logged in as admin), sign out first —
  // otherwise updatePassword would change that user's password, not the invitee's.
  useEffect(() => {
    if (!isLoaded || !isUserLoaded || !signIn || !setActive || !signOut) {
      return;
    }
    if (!signInToken || ticketHandledRef.current) {
      return;
    }

    const run = async () => {
      if (user) {
        if (signedOutForInviteRef.current) {
          return;
        }
        signedOutForInviteRef.current = true;
        setIsConsumingTicket(true);
        setError("");
        try {
          // Stay on the invite URL so the effect can consume the ticket after sign-out.
          clearAllStickyFilters();
          await signOut({
            redirectUrl: `${window.location.pathname}${window.location.search}`,
          });
        } catch (err) {
          setError(
            err.errors?.[0]?.longMessage ||
              "Unable to switch to the invite session. Please sign out and reopen the link."
          );
          signedOutForInviteRef.current = false;
          setIsConsumingTicket(false);
        }
        return;
      }

      ticketHandledRef.current = true;
      setIsConsumingTicket(true);
      setError("");

      try {
        const result = await signIn.create({
          strategy: "ticket",
          ticket: signInToken,
        });

        if (result.status === "complete" && result.createdSessionId) {
          await setActive({ session: result.createdSessionId });
          // Clerk tickets are single-use. Drop ?token= so a refresh keeps the
          // invitee session instead of signing out and re-consuming a spent ticket.
          navigate("/set-password", { replace: true });
        } else {
          setError(
            "Unable to activate your invite link. Please request a new invite or contact support."
          );
          ticketHandledRef.current = false;
        }
      } catch (err) {
        setError(
          err.errors?.[0]?.longMessage ||
            "Unable to activate your invite link. Please request a new invite or contact support."
        );
        ticketHandledRef.current = false;
      } finally {
        setIsConsumingTicket(false);
      }
    };

    run();
  }, [isLoaded, isUserLoaded, signIn, setActive, signOut, signInToken, user, navigate]);

  // Legacy email-only flow: send reset code (form still expects a signed-in user for updatePassword).
  // Skip when a sign-in token is present — that path establishes the session above.
  useEffect(() => {
    if (!isLoaded || !email || signInToken || user || !signIn) {
      return;
    }

    const sendResetCode = async () => {
      setIsLoading(true);
      setError("");
      try {
        await signIn.create({
          strategy: "reset_password_email_code",
          identifier: email,
        });
      } catch (err) {
        setError(err.errors?.[0]?.longMessage || "Failed to send reset code");
      } finally {
        setIsLoading(false);
      }
    };

    sendResetCode();
  }, [isLoaded, email, signInToken, user, signIn]);

  // Reset the user's password (requires an active Clerk session from the invite ticket).
  async function reset(e) {
    e.preventDefault();

    if (password !== confirmPassword) {
      setPasswordsMatch(false);
      return;
    }

    if (!user) {
      setError(
        "Your invite session is not active yet. Please wait a moment or reopen the link from your email."
      );
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await user.updatePassword({
        newPassword: password,
      });
      navigate("/");
    } catch (err) {
      setError(
        err.errors?.[0]?.longMessage || "An error occurred during password reset"
      );
    } finally {
      setIsLoading(false);
    }
  }

  if (!isLoaded || !isUserLoaded) {
    return null;
  }

  const showTicketLoading = Boolean(signInToken) && (isConsumingTicket || !user);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-12 sm:px-6 lg:px-8">
      {/* Set fixed width and height for square shape */}
      <div className="w-[400px] rounded-lg bg-white shadow-xl overflow-hidden flex flex-col">
        {/* Header section with fixed height */}
        <div className="p-4 flex flex-col items-center">
          <div className="flex justify-center items-center mt-2">
            <img src={LOGO} className="w-1/2 aspect-auto" alt="Logo" />
          </div>
          <h2 className="text-lg text-center font-bold mt-3 text-gray-800">
            Create Your Password
          </h2>
          <p className="text-sm text-center text-gray-500 mt-1">
            Choose a secure password to access your account
          </p>
        </div>

        {/* Form section with flex-grow to fill remaining space */}
        <div className="px-6 flex-grow overflow-y-auto">
          <div className="w-full">
            {showTicketLoading || (isLoading && !user && email && !signInToken) ? (
              <div className="flex flex-col items-center justify-center py-8">
                <svg
                  className="animate-spin h-10 w-10 text-indigo-600 mb-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <p className="text-sm text-gray-600">
                  {signInToken
                    ? "Activating your invite..."
                    : `Sending reset code to ${email}...`}
                </p>
              </div>
            ) : (
              <form onSubmit={reset} className="space-y-4">
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    New Password
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LockClosedIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </div>
                    <input
                      type="password"
                      id="password"
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-3 py-2 sm:text-sm border-gray-300 rounded-md border bg-white"
                      placeholder="Enter your new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Password must be at least 8 characters long
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Confirm Password
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LockClosedIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </div>
                    <input
                      type="password"
                      id="confirmPassword"
                      className={`focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-3 py-2 sm:text-sm border-gray-300 rounded-md border bg-white ${
                        !passwordsMatch ? "border-red-500" : ""
                      }`}
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                  {!passwordsMatch && (
                    <p className="mt-1 text-xs text-red-600">
                      Passwords do not match
                    </p>
                  )}
                </div>

                {error && (
                  <div className="rounded-md bg-red-50 p-3">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-red-400"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-red-800">
                          {error}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isLoading || isConsumingTicket || !passwordsMatch || !user}
                    className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm ${
                      isLoading || isConsumingTicket || !passwordsMatch || !user
                        ? "opacity-75 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Setting Password...
                      </>
                    ) : (
                      "Set Password"
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Footer section with fixed height */}
        <div className="bg-gray-50 px-6 py-3 flex justify-end mt-auto"></div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
